import { defineComponent as _defineComponent } from 'vue'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useIntersectionObserver } from '@vueuse/core'
import SmallPaymentMethodSelector from '@/components/Payment/SmallPaymentMethodSelector.vue'
import EprintLoadingOverlay from '@/components/format-edit/eprint/EprintLoadingOverlay.vue'
import EprintPaypayNoticeDialog from '@/components/format-edit/eprint/EprintPaypayNoticeDialog.vue'
import EprintPrintOptionRadio from '@/components/format-edit/eprint/EprintPrintOptionRadio.vue'
import { useEprintFunnel } from '@/composables/format-edit/useEprintFunnel'
import { useHostServices } from '@/composables/format-edit/useHostServices'
import {
  useEprintActions,
  useEprintDerived,
  useEprintSession
} from '@/composables/format-edit/useEprintSession'
import { isCashlessSupported } from '@/utils/format-edit/eprintOptions'

type PaymentSelectorVm = {
  isConvinienceButtonDisabled: boolean
  isCardButtonDisabled: boolean
  isPaypayButtonDisabled: boolean
}


export default /*#__PURE__*/_defineComponent({
  __name: 'EprintCheckoutPanel',
  setup(__props) {

const session = useEprintSession()
const actions = useEprintActions()
const { site } = useHostServices()
const funnel = useEprintFunnel()
const {
  currentPaymentPrice,
  currentTemplateConfig,
  recommendedOption,
  otherOptions,
  hasRecommendedOption,
  hasOtherOptions,
  isLoading,
  defaultEprintPaymentMethod
} = useEprintDerived(session)

const defaultPaymentMethod = computed(() =>
  defaultEprintPaymentMethod(site.appFlag)
)

const paymentSelector = ref<PaymentSelectorVm | null>(null)
const sectionPrintType = ref<HTMLElement | null>(null)
const sectionTerms = ref<HTMLElement | null>(null)
const sectionPayment = ref<HTMLElement | null>(null)

onMounted(() => {
  session.paymentSelectorApi = {
    resetDisabled() {
      const selector = paymentSelector.value
      if (!selector) return
      selector.isConvinienceButtonDisabled = false
      selector.isCardButtonDisabled = false
      selector.isPaypayButtonDisabled = false
    },
    lockAll() {
      const selector = paymentSelector.value
      if (!selector) return
      selector.isConvinienceButtonDisabled = true
      selector.isCardButtonDisabled = true
      selector.isPaypayButtonDisabled = true
    },
    unlockConvenience() {
      const selector = paymentSelector.value
      if (!selector) return
      selector.isConvinienceButtonDisabled = false
    }
  }
})
onBeforeUnmount(() => {
  session.paymentSelectorApi = null
})

function trackSection(name: string) {
  if (!session.funnelSessionId || !session.dialogLayoutReady) return
  if (session.viewedSections[name]) return
  session.viewedSections[name] = true
  funnel.send('section_view', {
    section: name,
    format_id: session.formatid,
    print_type: session.printType,
    is_cashless_available: isCashlessSupported({ id: session.printType }),
    funnel_session_id: session.funnelSessionId
  })
}

function maybeTrack(el: HTMLElement | null, name: string) {
  if (!el || !session.dialogLayoutReady) return
  const rect = el.getBoundingClientRect()
  if (rect.top < window.innerHeight && rect.bottom > 0) trackSection(name)
}

useIntersectionObserver(sectionPrintType, ([entry]) => {
  if (entry?.isIntersecting) trackSection('print_type')
})
useIntersectionObserver(sectionTerms, ([entry]) => {
  if (entry?.isIntersecting) trackSection('terms')
})
useIntersectionObserver(sectionPayment, ([entry]) => {
  if (entry?.isIntersecting) trackSection('payment_method')
})

watch(
  () => session.dialogLayoutReady,
  (ready) => {
    if (!ready) return
    maybeTrack(sectionPrintType.value, 'print_type')
    maybeTrack(sectionTerms.value, 'terms')
    maybeTrack(sectionPayment.value, 'payment_method')
  }
)

function handlePrintTypeChange(clickedId: string) {
  if (
    !session.dialogOpenedAt ||
    Date.now() - session.dialogOpenedAt.getTime() < 500
  ) {
    return
  }
  if (session.funnelSessionId) {
    const isRecheck =
      session.previousClickedPrintType !== null &&
      String(session.previousClickedPrintType) === String(clickedId)
    funnel.send('scroll_to_payment', {
      print_type: String(clickedId),
      is_cashless: isCashlessSupported({ id: clickedId }),
      is_recheck: isRecheck,
      format_id: session.formatid,
      funnel_session_id: session.funnelSessionId
    })
    session.previousClickedPrintType = clickedId
  }
  nextTick(() => {
    scrollChildIntoScrollableAncestor(sectionPayment.value)
  })
}

/** 見えている支払い欄へはスクロールしない（overlay を上端へ巻き上げない）。 */
function scrollChildIntoScrollableAncestor(target: HTMLElement | null) {
  if (!target) return
  let container: HTMLElement | null = target.parentElement
  while (container && container !== document.documentElement) {
    const overflowY = window.getComputedStyle(container).overflowY
    const canScroll =
      (overflowY === 'auto' || overflowY === 'scroll') &&
      container.scrollHeight > container.clientHeight + 1
    if (canScroll) {
      const cRect = container.getBoundingClientRect()
      const tRect = target.getBoundingClientRect()
      if (tRect.top >= cRect.top && tRect.bottom <= cRect.bottom) return
      const delta =
        tRect.top < cRect.top
          ? tRect.top - cRect.top
          : tRect.bottom - cRect.bottom
      container.scrollTo({
        top: container.scrollTop + delta,
        behavior: 'smooth'
      })
      return
    }
    container = container.parentElement
  }
}

function handleOtherSizesToggle(activeNames: string[]) {
  if (!session.funnelSessionId) return
  funnel.send('other_sizes_toggle', {
    action: activeNames && activeNames.length > 0 ? 'open' : 'close',
    print_type: String(session.printType),
    format_id: session.formatid,
    funnel_session_id: session.funnelSessionId
  })
}

function handleTermsLinkClick() {
  if (!session.funnelSessionId) return
  funnel.send('terms_link_click', {
    format_id: session.formatid,
    funnel_session_id: session.funnelSessionId
  })
}

return { __sfc: true,session, actions, site, funnel, currentPaymentPrice, currentTemplateConfig, recommendedOption, otherOptions, hasRecommendedOption, hasOtherOptions, isLoading, defaultEprintPaymentMethod, defaultPaymentMethod, paymentSelector, sectionPrintType, sectionTerms, sectionPayment, trackSection, maybeTrack, handlePrintTypeChange, scrollChildIntoScrollableAncestor, handleOtherSizesToggle, handleTermsLinkClick, SmallPaymentMethodSelector, EprintLoadingOverlay, EprintPaypayNoticeDialog, EprintPrintOptionRadio }
}

})